import { CompanyAddress, DeliveryAddress, DeliveryAddressMetadata, EnrichedProduct } from "@/types";
import { format } from "date-fns";
import { ProductDocument } from "@/prismicio-types";
import { Locales } from "@/lib/i18n";
import { StoreCustomerAddress, StoreProduct } from "@medusajs/types";
import { getPermalink } from "@/lib/i18n/functions";
import { isCustomerInfoRoute, isDeliveryRoute, isRecapRoute } from "@/components/templates/Checkout";
import { enUS, fr, nl } from "date-fns/locale";
import { isFilled, KeyTextField } from "@prismicio/client";
import { resolveProductConditioning, toLotPrice } from "@/lib/product-units";

export { cn } from "@/lib/classnames";

export const convertDate = (
  date: Date | string | undefined,
  formatString: string = "dd/MM/yyyy",
  locale: string = "en"
): string => {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "";

  const localeMap = {
    fr: fr,
    nl: nl,
    en: enUS,
  };

  const dateLocale = localeMap[locale as keyof typeof localeMap] || localeMap.en;

  return format(dateObj, formatString, { locale: dateLocale });
};

export const normalizeDate = (date: Date | string | undefined): string | undefined => {
  if (!date) return undefined;
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return undefined;

  const normalized = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 12, 0, 0, 0);

  return normalized.toISOString();
};

/**
 * This is type guard to check if the product is of EnrichedProduct type.
 * @param product - The product to check if it is enriched.
 * @returns
 */
export const isEnrichedProduct = (product: ProductDocument | EnrichedProduct): product is EnrichedProduct => {
  return typeof product === "object" && product !== null && "prismic" in product && "category" in product;
};

export const isMaintenanceMode = (slugs: string[]) => {
  return slugs.includes("maintenance");
};

export const getHomePageSlug = (locale: Locales) => {
  if (locale === "nl") {
    return "/nl";
  } else if (locale === "en") {
    return "/en";
  }
  return "/";
};

export interface PriceFormatOptions {
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  showDecimals?: boolean;
}

export const formatPrice = (price: number, options: PriceFormatOptions = {}) => {
  const {
    currency = "EUR",
    locale = "fr-FR",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showDecimals = true,
  } = options;

  const formatOptions: Intl.NumberFormatOptions = {
    style: "currency",
    currency: currency,
    minimumFractionDigits: showDecimals ? minimumFractionDigits : 0,
    maximumFractionDigits: showDecimals ? maximumFractionDigits : 0,
  };

  const formattedPrice = new Intl.NumberFormat(locale, formatOptions).format(price);

  return formattedPrice;
};

export const extractCurrencySymbol = (formattedPrice: string) => {
  const match = formattedPrice.match(/[^\d\s,.()-]+/);
  return match ? match[0].trim() : "";
};

export const removeCurrencySymbol = (formattedPrice: string) => {
  return formattedPrice
    .replace(/^[^\d\s,.()-]+\s*/, "")
    .replace(/\s*[^\d\s,.()-]+$/, "")
    .trim();
};

/**
 * This function retrieves the UIDs of products from an array of ProductDocument objects.
 * @param products - An array of ProductDocument objects or null.
 * @returns An array of product UIDs or an empty array.
 */
export const getCollectionProductUids = (products: ProductDocument[] | null) => products?.map((p) => p.uid) ?? [];

/**
 * This function retrieves the price of a Medusa product and formats it.
 * @param product - The Medusa product object.
 * @returns The formatted price as a string.
 */
export const getMedusaProductPrice = (product: StoreProduct | undefined) => {
  const variantPrice = (product?.variants && (product.variants[0]?.calculated_price?.calculated_amount as number)) || 0;
  const conditioning = resolveProductConditioning(
    product?.metadata?.conditioning,
    product?.metadata?.conditioning_bac
  );

  return formatPrice(toLotPrice(variantPrice, conditioning), { showDecimals: true });
};

export const getCalendarLocale = (locale: Locales) => {
  switch (locale) {
    case "en":
      return "English";
    case "fr":
      return "French";
    case "nl":
      return "Dutch";
    default:
      return "French";
  }
};

export const getCheckoutBackRoute = (locale: Locales, route: string) => {
  if (isCustomerInfoRoute(locale, route)) return getPermalink(locale, "checkout", "event-info");

  if (isDeliveryRoute(locale, route)) return getPermalink(locale, "checkout", "customer-info");

  if (isRecapRoute(locale, route)) return getPermalink(locale, "checkout", "delivery");

  return getPermalink(locale, "checkout", "event-info");
};

type AddressLike = StoreCustomerAddress | CompanyAddress;

export const getDifferentAddress = <TAddress extends AddressLike>(addresses: TAddress[] | undefined) => {
  const shippingAddress = addresses?.filter((address) => {
    const metadata = getAddressMetadata(address);
    return metadata.type === "shipping" || ("address_type" in address && address.address_type === "shipping");
  });
  const billingAddress = addresses?.filter((address) => getAddressMetadata(address).type === "billing");
  return { shippingAddress, billingAddress };
};

export type AddressMetadata = DeliveryAddressMetadata;

export const getAddressMetadata = (address?: AddressLike | DeliveryAddress | null): AddressMetadata => {
  return (address?.metadata || {}) as AddressMetadata;
};

export const getAddressAttributes = <TAddress extends AddressLike>(
  addresses?: TAddress[],
  billingAddressId?: string,
  deliveryAddressId?: string
) => {
  const billingAddress = addresses?.find((add) => add.id === billingAddressId);
  const deliveryAddress = addresses?.find((add) => add.id === deliveryAddressId);
  const deliveryAddressMetadata = getAddressMetadata(deliveryAddress);
  const billingAddressMetadata = getAddressMetadata(billingAddress);

  return {
    billingAddress,
    deliveryAddress,
    billingAddressMetadata,
    deliveryAddressMetadata,
  };
};

export function extractKeyTextValue(keyTextField: KeyTextField | undefined): string {
  if (isFilled.keyText(keyTextField)) {
    return keyTextField;
  }
  return "";
}

export const isSaleProduct = (medusaProduct: StoreProduct | undefined) =>
  medusaProduct?.tags?.at(0)?.value === "Sale Product";
